import React, { useState, useEffect } from 'react';
import { CheckCircle2, ChevronRight, ChevronDown, Circle, List, X, Layers } from 'lucide-react';
import { AuditInstance, SEOModule, AtomicStep } from '../../types';
import { EngineService } from '../../services/engine';

interface SidebarProps {
  module: SEOModule;
  audit: AuditInstance;
  currentStepId: string;
  onSelectStep: (stepId: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  module,
  audit,
  currentStepId,
  onSelectStep,
}) => {
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);

  // Find which section contains currentStepId
  const findCurrentSectionId = () => {
    for (const sec of module.sections) {
      if (sec.steps.some(st => st.id === currentStepId)) {
        return sec.id;
      }
    }
    return module.sections[0]?.id;
  };

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => ({
    [findCurrentSectionId()]: true,
  }));

  // Automatically expand current section when currentStepId changes
  useEffect(() => {
    const secId = findCurrentSectionId();
    if (secId) {
      setExpandedSections(prev => ({
        ...prev,
        [secId]: true,
      }));
    }
  }, [currentStepId]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Calculate overall progress & find current step
  let totalStepsCount = 0;
  let completedStepsCount = 0;
  let currentStepObj: AtomicStep | null = null;

  for (const section of module.sections) {
    for (const step of section.steps) {
      totalStepsCount++;
      if (step.id === currentStepId) {
        currentStepObj = step;
      }
      const ans = audit.answers[step.id];
      if (ans && ans.completed) {
        completedStepsCount++;
      }
    }
  }

  const overallProgressPct = totalStepsCount > 0 ? Math.round((completedStepsCount / totalStepsCount) * 100) : 0;

  const handleStepClick = (stepId: string) => {
    onSelectStep(stepId);
    setIsMobileModalOpen(false);
  };

  const renderAccordionContent = () => (
    <div style={{ padding: '10px 8px', flex: 1 }}>
      {module.sections.map((section) => {
        const isExpanded = expandedSections[section.id] === true;

        let sectionCompleted = 0;
        section.steps.forEach(st => {
          if (audit.answers[st.id]?.completed) sectionCompleted++;
        });
        const isSectionFullyComplete = sectionCompleted === section.steps.length;

        return (
          <div key={section.id} style={{ marginBottom: '6px' }}>
            {/* Section Header Accordion Button */}
            <button
              onClick={() => toggleSection(section.id)}
              style={{
                width: '100%',
                padding: '9px 10px',
                background: isExpanded ? '#090e1a' : 'transparent',
                border: '1px solid',
                borderColor: isExpanded ? '#1e293b' : 'transparent',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '12.5px', overflow: 'hidden' }}>
                {isExpanded ? <ChevronDown size={15} color="#38bdf8" /> : <ChevronRight size={15} color="#64748b" />}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{section.title}</span>
              </div>
              
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  fontFamily: 'var(--font-mono)',
                  color: isSectionFullyComplete ? '#10b981' : '#38bdf8',
                  background: isSectionFullyComplete ? 'rgba(16, 185, 129, 0.15)' : 'rgba(2, 132, 199, 0.15)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  flexShrink: 0,
                  marginLeft: '6px',
                }}
              >
                {isSectionFullyComplete ? `✓ ${sectionCompleted}/${section.steps.length}` : `${sectionCompleted}/${section.steps.length}`}
              </span>
            </button>

            {/* Steps Accordion List */}
            {isExpanded && (
              <div style={{ marginLeft: '10px', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px', borderLeft: '1px solid #1e293b', paddingLeft: '8px' }}>
                {section.steps.map((step: AtomicStep) => {
                  const isCurrent = step.id === currentStepId;
                  const isCompleted = audit.answers[step.id]?.completed === true;
                  const isUnlocked = EngineService.isStepUnlocked(step, audit, module);

                  return (
                    <button
                      key={step.id}
                      disabled={!isUnlocked && !isCompleted}
                      onClick={() => handleStepClick(step.id)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        background: isCurrent ? 'rgba(2, 132, 199, 0.18)' : 'transparent',
                        border: '1px solid',
                        borderColor: isCurrent ? 'rgba(56, 189, 248, 0.4)' : 'transparent',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        textAlign: 'left',
                        cursor: isUnlocked || isCompleted ? 'pointer' : 'not-allowed',
                        opacity: isUnlocked || isCompleted ? 1 : 0.45,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                        {isCompleted ? (
                          <CheckCircle2 size={14} color="#10b981" />
                        ) : isCurrent ? (
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#38bdf8', display: 'inline-block', flexShrink: 0 }} />
                        ) : (
                          <Circle size={12} color="#64748b" />
                        )}

                        <span
                          style={{
                            fontSize: '12.5px',
                            fontWeight: isCurrent ? 700 : 500,
                            color: isCurrent ? '#ffffff' : isCompleted ? '#94a3b8' : '#64748b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {step.stepNumber}. {step.title}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {/* MOBILE ONLY: Top Step Selector Bar */}
      <div
        className="mobile-only"
        onClick={() => setIsMobileModalOpen(true)}
        style={{
          background: '#090e1a',
          border: '1px solid #1e293b',
          borderRadius: '12px',
          padding: '12px 14px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
          <div
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              background: 'rgba(2, 132, 199, 0.15)',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#38bdf8',
              flexShrink: 0,
            }}
          >
            <List size={16} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '10.5px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              Step {currentStepObj?.stepNumber || 1} of {totalStepsCount} · {overallProgressPct}% Complete
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentStepObj?.title || 'Audit Steps'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#38bdf8', fontSize: '12px', fontWeight: 700, flexShrink: 0, background: 'rgba(2, 132, 199, 0.1)', padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
          <span>All Steps</span>
          <ChevronDown size={14} />
        </div>
      </div>

      {/* MOBILE ONLY: Slide-in / Modal Drawer for 32 Steps */}
      {isMobileModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(6px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
          onClick={() => setIsMobileModalOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '500px',
              maxHeight: '85vh',
              background: '#070a12',
              border: '1px solid #1e293b',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.9)',
              overflow: 'hidden',
            }}
          >
            {/* Drawer Header */}
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #1e293b',
                background: '#090e1a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Layers size={18} color="#38bdf8" />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>
                    Audit Step Navigator
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                    {completedStepsCount} of {totalStepsCount} completed ({overallProgressPct}%)
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsMobileModalOpen(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'rgba(30, 41, 59, 0.6)',
                  border: '1px solid #1e293b',
                  color: '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Scrollable Step Tree */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px', WebkitOverflowScrolling: 'touch' }}>
              {renderAccordionContent()}
            </div>
          </div>
        </div>
      )}

      {/* DESKTOP ONLY: Fixed Left Sidebar */}
      <aside className="sidebar desktop-only" style={{ width: '310px', background: '#070a12', borderRight: '1px solid #1e293b', flexShrink: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        {/* Audit Progress Header */}
        <div
          style={{
            padding: '20px 16px',
            borderBottom: '1px solid #1e293b',
            background: '#090e1a',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              AUDIT NAVIGATOR
            </span>
            <span style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', fontFamily: 'var(--font-mono)' }}>
              {overallProgressPct}%
            </span>
          </div>

          {/* Progress Bar */}
          <div
            style={{
              height: '6px',
              background: '#1e293b',
              borderRadius: '3px',
              overflow: 'hidden',
              marginBottom: '8px',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${overallProgressPct}%`,
                background: 'linear-gradient(90deg, #0284c7 0%, #38bdf8 100%)',
                borderRadius: '3px',
                transition: 'width 0.3s ease',
              }}
            />
          </div>

          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
            {completedStepsCount} of {totalStepsCount} guided steps completed
          </div>
        </div>

        {/* Accordion Section List */}
        {renderAccordionContent()}
      </aside>
    </>
  );
};

